import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BlockTypeEnum } from '@omnea-app/core/blocks'
import { RequestStepStateEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { ValidUuidString, isUuid } from '@omnea-app/packages/universal/string-utils/isUuid'
import { RequestPriorityEnum } from '@omnea-app/requests/requestPriority'
import { TaskStatusEnum } from '@omnea-app/requests/requestsFrontendClient'
import { TagWithIdAndName } from '@omnea-app/tags/tagsFrontendClient'
import { TasksTopLevelFilterKey } from '@omnea-app/tasks/tasksFrontendClient'
import { Task } from '@omnea-app/tasks/tasksHttpClient'
import { useTasks } from '../useTasks'

type RequestStepTask = Extract<Task, { type: 'REQUEST_STEP' }>
type GlobalTask = Extract<Task, { type: 'GLOBAL' }>

export type TaskRow = {
  id: string
  requestId: string
  globalSubtype: GlobalTask['subtype'] | null
  stageType: BlockTypeEnum | null
  name: string
  publicId: string
  department: string | undefined
  label: string
  supplier: { name?: string; logoUrl?: string | null } | null
  type: string
  dueDate: Date | null
  createdAt: string
  state: RequestStepStateEnum
  stage: number
  totalStages: number
  status: TaskStatusEnum
  isRejected: boolean
  activeAmendmentRequests: { id: string }[]
  buyerLegalEntity: RequestStepTask['request']['buyerLegalEntity']
  value: RequestStepTask['request']['value']
  owner: RequestStepTask['request']['participants']['projectOwner']
  requestor: RequestStepTask['request']['participants']['requestor']
  tags: TagWithIdAndName[]
  autoGeneratedTags: TagWithIdAndName[]
  priority: RequestPriorityEnum
  currentUserAssignees: RequestStepTask['currentUserAssignees'] | null
  currentGroupAssignees: RequestStepTask['currentGroupAssignees'] | null
  allCurrentAssignees: string[]
  activeSubTaskIds: string[]
}

const toGlobalTaskRow = (task: GlobalTask, label: string): TaskRow => ({
  id: task.id,
  requestId: task.request.id,
  globalSubtype: task.subtype,
  name: task.request.name,
  publicId: task.request.publicId,
  department: task.request.departmentName,
  label,
  stageType: null,
  type: task.request.workflowTitle,
  supplier: { name: task.request.supplierName, logoUrl: task.request.supplierLogoUrl },
  dueDate: null,
  createdAt: task.createdAt,
  state: RequestStepStateEnum.RUNNING,
  stage: 0,
  totalStages: 0,
  status: TaskStatusEnum.IN_PROGRESS,
  isRejected: false,
  activeAmendmentRequests: [],
  buyerLegalEntity: task.request.buyerLegalEntity,
  value: task.request.value,
  owner: task.request.participants.projectOwner,
  requestor: task.request.participants.requestor,
  tags: task.request.tags,
  autoGeneratedTags: task.request.autoGeneratedTags,
  priority: task.request.priority as RequestPriorityEnum,
  currentUserAssignees: task.currentUserAssignees,
  currentGroupAssignees: [],
  allCurrentAssignees: task.currentUserAssignees.map((u) => `${u.firstName} ${u.lastName}`),
  activeSubTaskIds: [],
})

export const useTaskTableRowsForCurrentUser = (
  topLevelFilterKeyProp: TasksTopLevelFilterKey | ValidUuidString
): { rows: TaskRow[]; loading: boolean } => {
  const { t } = useTranslation('tasks')
  const topLevelFilterKey = useMemo(() => {
    if (isUuid(topLevelFilterKeyProp)) {
      return null
    }
    return topLevelFilterKeyProp
  }, [topLevelFilterKeyProp])

  const { data, isLoading: loading } = useTasks({ topLevelFilterKey })
  const tasks = data || []

  const globalRows = tasks
    .filter((task): task is GlobalTask => task.type === 'GLOBAL')
    .map((task) => toGlobalTaskRow(task, t(`global-tasks.${task.subtype}`)))

  // Rows from an older API deploy lack a `type` discriminator; treat anything not GLOBAL as a request step.
  const requestStepRows = tasks
    .filter((task): task is RequestStepTask => task.type !== 'GLOBAL')
    .map((task) => {
      return {
        id: task.id,
        requestId: task.request.id,
        globalSubtype: null,
        stageType: task.stageType as BlockTypeEnum,
        state: task.state as RequestStepStateEnum,
        stage: task.stage,
        totalStages: task.totalStages,
        status: task.status as TaskStatusEnum,
        isRejected: task.isRejected,
        name: task.request.name,
        label: task.stepLabel,
        supplier: {
          name: task.request.supplierName,
          logoUrl: task.request.supplierLogoUrl,
        },
        publicId: task.request.publicId,
        department: task.request.departmentName,
        type: task.request.workflowTitle,
        dueDate: new Date(task.dueDate),
        createdAt: task.createdAt,
        activeAmendmentRequests: task.activeAmendments,
        tags: task.request.tags,
        autoGeneratedTags: task.request.autoGeneratedTags,
        priority: task.request.priority as RequestPriorityEnum,
        buyerLegalEntity: task.request.buyerLegalEntity,
        value: task.request.value,
        owner: task.request.participants.projectOwner,
        requestor: task.request.participants.requestor,
        currentUserAssignees: task.currentUserAssignees,
        currentGroupAssignees: task.currentGroupAssignees,
        allCurrentAssignees: [
          ...task.currentUserAssignees.map((u) => `${u.firstName} ${u.lastName}`),
          ...task.currentGroupAssignees.map((g) => g.name),
        ],
        activeSubTaskIds: (task.subTasks || []).filter((subTask) => !subTask.completedOn).map((subTask) => subTask.id),
      }
    })

  return {
    rows: [...globalRows, ...requestStepRows],
    loading,
  }
}
